import { Form, message } from 'antd';
import { useRef, useState } from 'react';
import {
  buildXhsCreateFormData,
  buildXhsSummaryParams,
  buildXhsTagsParams,
  getXhsNotePreviewUrl,
  normalizeXhsImageFileList,
  readXhsNoteDragUid,
  reorderXhsImagesOnTarget,
  reorderXhsImagesToEnd,
  revokeXhsNotePreviewUrl,
} from '../contentListXhsFlow';

export const useContentListXhs = ({
  navigate,
  noActivePlan,
  storageActionBlocked,
  resolveErrorMessage,
  previewXhsNoteSummary,
  previewXhsNoteTags,
  createXhsNote,
  loadData,
  refreshQuota,
}) => {
  const [xhsNoteForm] = Form.useForm();
  const [xhsNoteModalVisible, setXhsNoteModalVisible] = useState(false);
  const [xhsNoteImageFileList, setXhsNoteImageFileList] = useState([]);
  const [xhsNoteCreating, setXhsNoteCreating] = useState(false);
  const [xhsNoteSummaryGenerating, setXhsNoteSummaryGenerating] = useState(false);
  const [xhsNoteTagsGenerating, setXhsNoteTagsGenerating] = useState(false);
  const [xhsNoteDragUid, setXhsNoteDragUid] = useState(null);
  const [xhsNoteDropHint, setXhsNoteDropHint] = useState(null);
  const xhsNotePreviewUrlByUidRef = useRef(new Map());

  const _revokeXhsNotePreviewUrl = (uid) => revokeXhsNotePreviewUrl(xhsNotePreviewUrlByUidRef, uid);
  const getPreviewUrl = (file) => getXhsNotePreviewUrl(xhsNotePreviewUrlByUidRef, file);
  const readDragUid = (e) => readXhsNoteDragUid(e);

  const openXhsModal = () => setXhsNoteModalVisible(true);

  const resetXhsNoteModal = () => {
    try {
      for (const f of xhsNoteImageFileList || []) {
        if (f?.uid) _revokeXhsNotePreviewUrl(f.uid);
      }
    } catch (_) {
      // ignore
    }
    setXhsNoteImageFileList([]);
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    xhsNoteForm.resetFields();
  };

  const closeXhsModal = () => {
    setXhsNoteModalVisible(false);
    resetXhsNoteModal();
  };

  const handleXhsNoteImagesChange = ({ fileList: newFileList }) => {
    const { next, truncated } = normalizeXhsImageFileList(newFileList);
    if (truncated) {
      message.warning('最多支持 18 张图片，已自动截断');
    }
    setXhsNoteImageFileList(next);
  };

  const handleXhsNoteRemoveImage = (uid) => {
    setXhsNoteImageFileList((prev) => (prev || []).filter((f) => f.uid !== uid));
    _revokeXhsNotePreviewUrl(uid);
    if (xhsNoteDragUid === uid) setXhsNoteDragUid(null);
    if (xhsNoteDropHint?.uid === uid) setXhsNoteDropHint(null);
  };

  const handleXhsNoteDropOn = (targetUid, position = 'before', fromUidOverride = null) => {
    const fromUid = fromUidOverride || xhsNoteDragUid;
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    if (!fromUid || !targetUid || fromUid === targetUid) return;
    setXhsNoteImageFileList((prev) => reorderXhsImagesOnTarget({ list: prev || [], fromUid, targetUid, position }));
  };

  const handleXhsNoteDropToEnd = (fromUidOverride = null) => {
    const fromUid = fromUidOverride || xhsNoteDragUid;
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    if (!fromUid) return;
    setXhsNoteImageFileList((prev) => reorderXhsImagesToEnd({ list: prev || [], fromUid }));
  };

  const handleGenerateXhsNoteSummary = async () => {
    try {
      const v = xhsNoteForm.getFieldsValue(true) || {};
      const body = String(v.body || '').trim();
      if (!body) {
        message.warning('请先填写正文');
        return;
      }
      setXhsNoteSummaryGenerating(true);
      const resp = await previewXhsNoteSummary(buildXhsSummaryParams(v));
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '生成失败'));
      }
      const summary = String(resp?.summary || '').trim();
      xhsNoteForm.setFieldsValue({ summary });
      message.success('已生成简介');
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteSummaryGenerating(false);
    }
  };

  const handleGenerateXhsNoteTags = async () => {
    try {
      const v = xhsNoteForm.getFieldsValue(true) || {};
      const body = String(v.body || '').trim();
      if (!body) {
        message.warning('请先填写正文');
        return;
      }
      setXhsNoteTagsGenerating(true);
      const resp = await previewXhsNoteTags(buildXhsTagsParams(v));
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '生成失败'));
      }
      const tags = Array.isArray(resp?.tags) ? resp.tags : [];
      xhsNoteForm.setFieldsValue({ tags });
      message.success('已生成标签');
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteTagsGenerating(false);
    }
  };

  const handleCreateXhsNote = async () => {
    if (xhsNoteCreating) return;
    if (storageActionBlocked) {
      const text = noActivePlan
        ? '当前订阅未生效，请先购买或续费。'
        : '存储空间额度已用完，请前往「付费订阅」升级套餐。';
      message.warning(`${text} 正在前往付费订阅页面...`);
      navigate('/billing');
      return;
    }
    try {
      const values = await xhsNoteForm.validateFields();
      if (!xhsNoteImageFileList || xhsNoteImageFileList.length === 0) {
        message.warning('请至少上传 1 张图片');
        return;
      }
      setXhsNoteCreating(true);

      const fd = buildXhsCreateFormData({ values, imageFileList: xhsNoteImageFileList });
      const resp = await createXhsNote(fd);
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '创建失败'));
      }

      message.success('已创建小红书笔记内容');
      closeXhsModal();
      await loadData();
      refreshQuota().catch(() => {});
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteCreating(false);
    }
  };

  return {
    xhsNoteForm,
    xhsNoteModalVisible,
    openXhsModal,
    closeXhsModal,
    xhsNoteImageFileList,
    xhsNoteCreating,
    xhsNoteSummaryGenerating,
    xhsNoteTagsGenerating,
    xhsNoteDragUid,
    setXhsNoteDragUid,
    xhsNoteDropHint,
    setXhsNoteDropHint,
    handleXhsNoteImagesChange,
    handleXhsNoteRemoveImage,
    handleGenerateXhsNoteSummary,
    handleGenerateXhsNoteTags,
    handleCreateXhsNote,
    getPreviewUrl,
    readDragUid,
    handleXhsNoteDropOn,
    handleXhsNoteDropToEnd,
  };
};
